import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, Save, Trash2 } from 'lucide-react'
import { createAccount, deleteAccount, getAccounts, getControlAccounts } from '../../api/master'
import { getOpeningBalances, getStatementClasses, saveOpeningBalances } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { acct, glHref } from '../accounting/reportsShared'
import { localToday } from '../../utils/format'
import { ACCOUNT_TYPES, DEFAULT_LABEL, Field } from './masterShared'
import { returnTo } from '../../components/CreateOption'

/** The next unused code under a heading: 6000 → 6001, 6002, … */
const nextCode = (heading, accounts) => {
  const base = String(heading?.account_code || '')
  if (!/^\d{4}$/.test(base)) return ''
  const used = new Set(accounts.map((a) => String(a.account_code)))
  for (let n = Number(base) + 1; n < Number(base) + 100; n += 1) if (!used.has(String(n))) return String(n)
  return ''
}

// One account: its code, name, type and heading; what it holds and where it
// shows. Code and type are fixed once anything uses the account.
export default function AccountForm() {
  const { accountId } = useParams()
  const isNew = !accountId
  const [params] = useSearchParams()
  const nav = useNavigate()
  // Opened from "+ Add a new …" in a dropdown: go back there afterwards.
  const done = returnTo(useLocation().search, '/master/accounts')
  const qc = useQueryClient()
  const { notify } = useToast()
  const { data: accounts = [], isLoading } = useQuery({ queryKey: ['accounts'], queryFn: getAccounts })
  const { data: defaults = [] } = useQuery({ queryKey: ['control-accounts'], queryFn: getControlAccounts })
  const { data: classes } = useQuery({ queryKey: ['statement-classes'], queryFn: getStatementClasses })
  const account = accounts.find((a) => String(a.account_id) === String(accountId))
  const headings = useMemo(() => accounts.filter((a) => !a.parent_account_id && String(a.account_id) !== String(accountId)), [accounts, accountId])
  const [form, setForm] = useState(null)
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // Its opening balance, for moving books in: amount, debit or credit, and the date.
  const { data: ob } = useQuery({ queryKey: ['opening-balances', accountId || 'all'], queryFn: () => getOpeningBalances(accountId) })
  const obRow = !isNew ? (ob?.accounts || [])[0] : null
  const [opening, setOpening] = useState(null)
  const [createdId, setCreatedId] = useState(null)
  useEffect(() => {
    if (!ob || opening) return
    const o = obRow?.opening
    setOpening(o ? { amount: String(o.dr || o.cr), side: o.dr ? 'dr' : 'cr', date: o.date } : { amount: '', side: '', date: ob.date || localToday() })
  }, [ob, obRow, opening])

  useEffect(() => {
    if (isLoading || form) return
    if (isNew) {
      const parent = accounts.find((a) => String(a.account_id) === String(params.get('parent') || ''))
      setForm({ parent: parent ? String(parent.account_id) : '', code: parent ? nextCode(parent, accounts) : '', name: '', type: parent?.type || 'Expense' })
    } else if (account) {
      setForm({ parent: account.parent_account_id ? String(account.parent_account_id) : '', code: account.account_code, name: account.account_name, type: account.type })
    }
  }, [isLoading, isNew, account, accounts, params, form])

  if (isLoading || (!form && (isNew || account))) return <div className="ci-form-page"><Spinner label="Loading account…" /></div>
  if (!isNew && !account) {
    return <div className="ci-form-page"><DocHeader back={<BackLink to={done} label={done === '/master/accounts' ? 'Chart of accounts' : 'Back'} />} title="Account" /><ErrorBanner message="This account wasn’t found." /></div>
  }

  const kids = account ? accounts.filter((a) => a.parent_account_id === account.account_id) : []
  const roles = account ? defaults.filter((d) => String(d.account_code || '').trim() === account.account_code).map((d) => DEFAULT_LABEL[d.name] || d.name) : []
  const locked = !isNew && (account.has_postings || roles.length > 0)
  const parent = accounts.find((a) => String(a.account_id) === form.parent)
  const section = account ? (classes?.accounts || []).find((c) => c.code === account.account_code)?.section : ''
  const set = (patch) => setForm((f) => ({ ...f, ...patch }))
  const accountType = parent ? parent.type : form.type
  const normalSide = accountType === 'Asset' || accountType === 'Expense' ? 'dr' : 'cr'
  // Headings and Opening balance equity itself don't take an opening balance.
  const canOpen = isNew ? !!form.parent && !/^\d{2}00$/.test(form.code.trim()) : !(kids.length || !account.parent_account_id) && !obRow?.is_equity_account

  const pickParent = (id) => {
    const p = accounts.find((a) => String(a.account_id) === id)
    set({ parent: id, ...(p ? { type: p.type } : {}), ...(isNew && p ? { code: nextCode(p, accounts) } : {}) })
  }

  const save = async () => {
    const e = {}
    if (!form.code.trim()) e.code = 'Give it a code.'
    else if (accounts.some((a) => a.account_code === form.code.trim() && String(a.account_id) !== String(accountId))) e.code = 'Another account has this code.'
    if (!form.name.trim()) e.name = 'Give it a name.'
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    const amount = Math.round((Number(opening?.amount) || 0) * 100) / 100
    if (canOpen && amount && !opening?.date) { setError('Choose the date of its opening balance.'); return }
    setSaving(true)
    setError('')
    let id = createdId || (isNew ? null : Number(accountId))
    try {
      const res = await createAccount({
        account_id: id || undefined, account_code: form.code.trim(), account_name: form.name.trim(),
        type: accountType, parent_account_id: form.parent ? Number(form.parent) : null,
        new: !id || undefined,
      })
      id = id || res?.account_id
      if (!createdId && isNew) setCreatedId(id)
      // Then its opening balance, when it changed.
      const was = obRow?.opening
      const side = opening?.side || normalSide
      const dr = side === 'dr' ? amount : 0
      const cr = side === 'cr' ? amount : 0
      if (canOpen && id && (dr !== (was?.dr || 0) || cr !== (was?.cr || 0) || (was && amount && was.date !== opening.date))) {
        try {
          await saveOpeningBalances({ entry_date: opening.date, entries: [{ account_id: id, dr, cr }] })
        } catch (err) {
          await qc.invalidateQueries({ queryKey: ['accounts'] })
          setError(`The account is saved, but its opening balance isn’t: ${err?.response?.data?.error || err.message}`)
          return
        }
        qc.invalidateQueries({ queryKey: ['opening-balances'] })
      }
      await qc.invalidateQueries({ queryKey: ['accounts'] })
      qc.invalidateQueries({ queryKey: ['statement-classes'] })
      notify(isNew ? `${form.code.trim()} ${form.name.trim()} added` : 'Account saved')
      nav(done)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Couldn’t save the account.')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    try {
      await deleteAccount(accountId)
      await qc.invalidateQueries({ queryKey: ['accounts'] })
      notify(`${account.account_code} deleted`)
      nav(done)
    } catch (err) {
      setConfirmDelete(false)
      setError(err?.response?.data?.error || err.message || 'Couldn’t delete the account.')
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to={done} label={done === '/master/accounts' ? 'Chart of accounts' : 'Back'} />}
        title={isNew ? 'New account' : `${account.account_code} · ${account.account_name}`}
        badge={!isNew && (kids.length || !account.parent_account_id ? <StatusBadge label="Heading" tone="muted" /> : null)}
        meta={isNew ? 'An account in your books.' : `${account.type}${section ? ` · ${section}` : ''}`}
        actions={!isNew && account.has_postings && <Link className="ci-btn ci-btn-ghost" to={glHref(account.account_code)}><BookOpen size={15} />General ledger</Link>}
      />
      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card mf-form">
        <div className="ci-form-meta-top two">
          <Field label="Under heading" hint={kids.length ? 'It has accounts under it, so it stays a heading.' : (form.parent ? `It takes the heading’s type (${parent?.type?.toLowerCase()}).` : 'None: this is a heading account.')}>
            <select value={form.parent} disabled={kids.length > 0} onChange={(e) => pickParent(e.target.value)}>
              <option value="">None (a heading account)</option>
              {ACCOUNT_TYPES.map(([t, label]) => {
                const list = headings.filter((h) => h.type === t && (!locked || t === form.type))
                return list.length ? (
                  <optgroup key={t} label={label}>
                    {list.map((h) => <option key={h.account_id} value={h.account_id}>{h.account_code} · {h.account_name}</option>)}
                  </optgroup>
                ) : null
              })}
            </select>
          </Field>
          <Field label="Type">
            <select value={parent ? parent.type : form.type} disabled={!!parent || locked} onChange={(e) => set({ type: e.target.value })}>
              {ACCOUNT_TYPES.map(([t]) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <div className="ci-form-meta-top two">
          <Field label="Code" error={errors.code} hint={locked ? 'Fixed: the account is in use.' : (parent ? `Accounts under ${parent.account_code} run ${parent.account_code.slice(0, 2)}01–${parent.account_code.slice(0, 2)}99.` : 'Headings end in 00, e.g. 6000.')}>
            <input value={form.code} disabled={locked} onChange={(e) => set({ code: e.target.value.replace(/\s/g, '') })} inputMode="numeric" placeholder="e.g. 6016" />
          </Field>
          <Field label="Name" error={errors.name}>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Rent" />
          </Field>
        </div>
        {locked && (
          <p className="mf-form-note">
            Its code and type are fixed because {account.has_postings ? 'entries are posted to it' : 'it’s a default account'}. To reorganise, add a new account and move the balance with a <Link to="/accounting/journal-entries">journal entry</Link>.
          </p>
        )}
      </div>

      {canOpen && opening && (
        <div className="ci-form-meta-card mf-form">
          <h2 className="mf-form-title">Opening balance <HelpTip text="What this account held on the day you started using ASYS, if you’re moving your books in. The other side goes to Opening balance equity. Leave it blank if the account starts at zero." /></h2>
          <div className="ci-form-meta-top three">
            <Field label="Balance (optional)">
              <input value={opening.amount} inputMode="decimal" placeholder="0.00" disabled={obRow?.locked}
                onChange={(e) => setOpening((o) => ({ ...o, amount: e.target.value.replace(/[^\d.]/g, '') }))} />
            </Field>
            <Field label="Debit or credit" hint={`A ${accountType.toLowerCase()} account usually has a ${normalSide === 'dr' ? 'debit' : 'credit'} balance.`}>
              <select value={opening.side || normalSide} disabled={obRow?.locked} onChange={(e) => setOpening((o) => ({ ...o, side: e.target.value }))}>
                <option value="dr">Debit</option>
                <option value="cr">Credit</option>
              </select>
            </Field>
            <Field label="As of">
              <input type="date" value={opening.date} disabled={obRow?.locked} onChange={(e) => setOpening((o) => ({ ...o, date: e.target.value }))} />
            </Field>
          </div>
          <p className="mf-form-note">
            {obRow?.locked ? 'Its month is closed, so it can’t change here; use a journal entry. ' : ''}
            Moving a whole chart? Enter every balance at once under <Link to="/master/opening-balances">Opening balances</Link>, which checks that debits equal credits.
          </p>
        </div>
      )}

      {!isNew && (
        <div className="ci-form-meta-card mf-form">
          <dl className="mf-detail mf-acct-facts">
            <div className="mf-detail-row"><dt>Balance <HelpTip k="ledgerBalance" /></dt><dd>{account.has_postings ? acct(account.current_balance) : 'Nothing posted yet'}</dd></div>
            <div className="mf-detail-row"><dt>On the statements</dt><dd>{section || account.type} · <Link to="/accounting/reports/classification">change</Link></dd></div>
            <div className="mf-detail-row"><dt>Used for</dt><dd>{roles.length ? roles.join(', ') : 'Nothing set'} · <Link to="/master/control-accounts">transaction accounts</Link></dd></div>
            {kids.length > 0 && <div className="mf-detail-row"><dt>Accounts under it</dt><dd>{kids.length}</dd></div>}
          </dl>
        </div>
      )}

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add account' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(done)}>Cancel</button>
        </div>
        {!isNew && !locked && kids.length === 0 && (
          <button type="button" className="ci-btn ci-btn-ghost ci-btn-danger" onClick={() => setConfirmDelete(true)}><Trash2 size={14} />Delete</button>
        )}
      </div>

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} title={`Delete ${account?.account_code} ${account?.account_name}?`} size="sm"
        footer={<><Button variant="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button><Button variant="danger" onClick={remove}>Delete</Button></>}>
        <p className="acc-modal-text">Nothing has been posted to it. It’s removed from the chart of accounts for good.</p>
      </Modal>
    </div>
  )
}
